/**
 * Service worker registration.
 *
 * Registered only for real (non-emulator) builds: a stale cached shell during
 * development is a debugging trap, and the emulator setup changes often.
 *
 * The worker is deliberately conservative — see public/sw.js for what it
 * refuses to cache.
 */
import { isDev } from './env.js';
import { isNative } from './device.js';

export interface PwaStatus {
  supported: boolean;
  registered: boolean;
  /** A newer version is waiting; the app takes it on the next load. */
  updateReady: boolean;
}

let status: PwaStatus = { supported: false, registered: false, updateReady: false };

export function pwaStatus(): PwaStatus {
  return status;
}

export async function registerServiceWorker(): Promise<PwaStatus> {
  // Native ships its assets inside the binary. A worker there (Android serves
  // https://localhost, so it would register) caches the old shell and keeps
  // serving it after an app update.
  const supported = 'serviceWorker' in navigator && window.isSecureContext && !isNative();
  status = { ...status, supported };
  if (!supported || isDev) return status;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    status = { ...status, registered: true };

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          status = { ...status, updateReady: true };
        }
      });
    });
  } catch {
    // A failed registration must never block the app; it just means no offline.
    status = { ...status, registered: false };
  }
  return status;
}

/**
 * True when the app is running from the home screen rather than a browser tab.
 * Used to decide whether to show the "add to home screen" hint.
 */
export function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone;
}
